import { inject, Injectable } from '@angular/core';
import {
  type ActivatedRouteSnapshot,
  DefaultUrlSerializer,
  type NavigationBehaviorOptions,
  type NavigationStart,
  Router,
  type UrlTree,
} from '@angular/router';

import type { HttpUrl, RouteBase, RouteUrl } from '@kernel';

import { LogService } from '@core/log';

import {
  type NavContext,
  NavIntent,
  type QueryParams,
  type TabBase,
  type TabKey,
} from './nav.types';
import { encodeRouteSegments, findRootSnapshot, isRootSnapshot } from './nav.util';

const NAV_TAB_ID_KEY = 'navTabId';

export type NavOptions = NavigationBehaviorOptions & {
  readonly intent?: NavIntent;
  readonly tabId?: string;
};

@Injectable({ providedIn: 'root' })
export class NavRouteService {
  private readonly router = inject(Router);
  private readonly log = inject(LogService);
  private readonly serializer = new DefaultUrlSerializer();

  private context: NavContext = { intent: null, tabId: null };

  createKey(url: string): TabKey | null {
    const tree = this.parse(url);

    if (!tree) return null;

    const path = this.normalizePath(tree);

    if (!path) return null;

    return { path, queryParams: this.normalizeParams(tree), fragment: tree.fragment };
  }

  createKeyFromSnapshot(route: ActivatedRouteSnapshot): TabKey | null {
    const path = encodeRouteSegments(route.pathFromRoot.flatMap((r) => r.url.map((s) => s.path)));

    if (!path) return null;

    const queryParams: QueryParams = {};

    for (const k of route.queryParamMap.keys)
      queryParams[k] = [...route.queryParamMap.getAll(k)].sort();

    return {
      path: `/${path}` as RouteBase,
      queryParams,
      fragment: route.fragment,
    };
  }

  createTabIdentityFromSnapshot(
    route: ActivatedRouteSnapshot,
  ): Pick<TabBase, 'key' | 'isRoot'> | null {
    const root = findRootSnapshot(route);
    const isRoot = isRootSnapshot(root);
    const keyObj = this.createKeyFromSnapshot(isRoot ? root : route);

    if (!keyObj) return null;

    return {
      key: isRoot ? keyObj.path : this.getKeyString(keyObj),
      isRoot,
    };
  }

  matchesTabUrl(tab: Pick<TabBase, 'key' | 'isRoot'>, url: string): boolean {
    const target = this.createKey(url);

    if (!target) return false;

    if (!tab.isRoot) return tab.key === this.getKeyString(target);

    const root = this.createKey(tab.key);

    return !!root && (target.path === root.path || target.path.startsWith(`${root.path}/`));
  }

  getKeyString(key: TabKey): string {
    const params = this.serializeQueryParams(key.queryParams);
    const fragment = key.fragment ? `#${encodeURIComponent(key.fragment)}` : '';

    return !params && !fragment ? key.path : `${key.path}|${params}${fragment}`;
  }

  getKeyUrl(key: TabKey): RouteUrl {
    const params = this.serializeQueryParams(key.queryParams);
    const query = params ? `?${params}` : '';
    const fragment = key.fragment ? `#${encodeURIComponent(key.fragment)}` : '';

    return `${key.path}${query}${fragment}` as RouteUrl;
  }

  replaceParams(url: RouteUrl, queryParams: QueryParams): RouteUrl | null {
    const key = this.createKey(url);

    if (!key) return null;

    return this.getKeyUrl({ path: key.path, queryParams, fragment: key.fragment });
  }

  normalizeUrl(url: string): RouteUrl | null {
    const keyObj = this.createKey(url);

    return keyObj ? this.getKeyUrl(keyObj) : null;
  }

  async navigateByUrl(url: RouteUrl, options: NavOptions = {}): Promise<boolean> {
    const { info, intent = NavIntent.InPlace, state, tabId, ...navigationOptions } = options;
    const sameUrl = this.isSameUrl(url, this.router.url);
    const historyTabId = this.readTabId(
      typeof window === 'undefined' ? null : window.history.state,
    );

    if (sameUrl && (!tabId || !historyTabId || historyTabId === tabId)) return true;

    try {
      return await this.router.navigateByUrl(url, {
        ...navigationOptions,
        ...(sameUrl ? { onSameUrlNavigation: 'reload' as const } : {}),
        info: {
          ...(this.isRecord(info) ? info : {}),
          navIntent: intent,
          ...(tabId ? { tabId } : {}),
        },
        state: {
          ...state,
          ...(tabId ? { [NAV_TAB_ID_KEY]: tabId } : {}),
        },
      });
    } catch (err) {
      this.log.error('[NavRouteService] Navigation failed', err);

      return false;
    }
  }

  captureContext(event: NavigationStart): void {
    const info = this.router.currentNavigation()?.extras.info;
    const state = this.router.currentNavigation()?.extras.state;
    const intent =
      this.isRecord(info) && this.isIntent(info['navIntent']) ? info['navIntent'] : null;
    const tabId =
      (this.isRecord(info) && typeof info['tabId'] === 'string' ? info['tabId'] : null) ??
      this.readTabId(event.restoredState) ??
      this.readTabId(state);

    this.context = { intent, tabId };
  }

  getCurrentContext(): NavContext {
    return this.context;
  }

  clearContext(): void {
    this.context = { intent: null, tabId: null };
  }

  stampCurrentTab(tabId: string): void {
    if (typeof window === 'undefined') return;
    if (this.readTabId(window.history.state) === tabId) return;

    window.history.replaceState(
      { ...window.history.state, [NAV_TAB_ID_KEY]: tabId },
      '',
      window.location.href,
    );
  }

  openExternalLink(url: HttpUrl, sameTab = false): void {
    if (typeof window === 'undefined') return;

    window.open(url, sameTab ? '_self' : '_blank', 'noopener,noreferrer');
  }

  private parse(url: string): UrlTree | null {
    try {
      return this.serializer.parse(url);
    } catch {
      return null;
    }
  }

  private normalizePath(tree: UrlTree): RouteBase | null {
    const segments = tree.root.children['primary']?.segments ?? [];
    const path = encodeRouteSegments(segments.map((s) => s.path));

    return path ? (`/${path}` as RouteBase) : null;
  }

  private normalizeParams(tree: UrlTree): QueryParams {
    const out: QueryParams = {};

    for (const [key, value] of Object.entries(tree.queryParams)) {
      if (value === null || value === undefined) continue;

      out[key] = Array.isArray(value) ? value.map(String).sort() : [String(value)];
    }

    return out;
  }

  private serializeQueryParams(queryParams: QueryParams): string {
    return Object.entries(queryParams)
      .sort(([a], [b]) => a.localeCompare(b))
      .flatMap(([key, values]) =>
        values.map((value) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`),
      )
      .join('&');
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private isIntent(value: unknown): value is NavIntent {
    return value === NavIntent.InPlace || value === NavIntent.TabSwitch;
  }

  private readTabId(value: unknown): string | null {
    return this.isRecord(value) && typeof value[NAV_TAB_ID_KEY] === 'string'
      ? value[NAV_TAB_ID_KEY]
      : null;
  }

  private isSameUrl(target: string, current: string): boolean {
    const targetKey = this.createKey(target);
    const currentKey = this.createKey(current);

    if (!targetKey || !currentKey) return false;

    return this.getKeyString(targetKey) === this.getKeyString(currentKey);
  }
}
